import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import findKey from 'lodash/findKey';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { GetRights, GetMyPrincipal } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights, hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { PageTitle, PageContent, Tab, Link, Menu, Flex } from 'nallian-shared-ui/lib/components';
import organisationService from '../services/organisationService';
import AppSubscriptions from './appSubscriptions/AppSubscriptions';
import IdentificationCodesTable from './identificationcodes/IdentificationCodesTable';
import { TABS } from '../constants';
import PrincipalsOverview from './principals/PrincipalsOverview';
import PendingInvitations from './pendingInvitations/PendingInvitations';
import UserGroupsOverview from './userGroups/UserGroupsOverview';
import JoinRequestsOverview from './joinRequests/JoinRequestsOverview';
import { isSystemOrganisation } from '../../../app/config/systemOrgs';
import { useCanAccessIdentificationCodes } from '../hooks/useCanAccessIdentificationCodes';
import organisationAdminsService from '../services/organisation/organisationAdminsService';
import OrganisationContactsService from '../services/contacts/organisationContactsService';
import Details from './details/Details';
import OrganisationCommunities from './communities/OrganisationCommunities';
import DocumentsOverviewTab from './documents/DocumentsOverviewTab';
import ImpersonationRequestsTab from './impersonation/ImpersonationRequestsTab';

const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.READ.ALL',
  'PRINCIPALS.READ.ORGANISATION',
  'PRINCIPALS.READ.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const PENDING_INVITATION_RIGHTS = [
  'PENDINGINVITATIONS.MANAGE.ALL',
  'PENDINGINVITATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PENDINGINVITATIONS.READ.ALL',
  'PENDINGINVITATIONS.READ.ORGANISATION',
  'PENDINGINVITATIONS.MANAGE.ORGANISATION',
  'PENDINGINVITATIONS.READ.ALL.EXCEPTSYSTEMORGS',
];

const ORGANISATION_APPS_RIGHTS = [
  'ORGANISATIONAPPS.MANAGE.ALL',
  'ORGANISATIONAPPS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'ORGANISATIONAPPS.MANAGE.ORGANISATION',
  'ORGANISATIONAPPS.READ.ALL',
  'ORGANISATIONAPPS.READ.ORGANISATION',
  'ORGANISATIONAPPS.READ.ALL.EXCEPTSYSTEMORGS',
];

const COMMUNITY_APPS_RIGHTS = [
  'COMMUNITYAPPS.MANAGE.ALL',
  'COMMUNITYAPPS.READ.ALL',
  'COMMUNITYAPPS.READ.COMMUNITY',
];

export const ORGANISATION_RIGHTS = [
  'ORGANISATIONS.MANAGE.ALL',
  'ORGANISATIONS.MANAGE.ORGANISATION',
  'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
];

const USER_GROUP_RIGHTS = [
  'user-groups/read/non-system-organisation',
  'user-groups/read/system-organisation',
  'user-groups/read/own-organisation',
];

const OrganisationDetails = ({
  organisationFetch,
  dispatchOrganisationGet,
  dispatchOrganisationAdminsGet,
  organisationAdminsFetch,
  dispatchOrganisationContactsGet,
  organisationContactsFetch,
}) => {
  const { t } = useTranslation();
  const { tab, id } = useParams();
  const navigate = useNavigate();

  const rights = useSelector(GetRights);
  const myPrincipal = useSelector(GetMyPrincipal);
  const organisationId = id || myPrincipal?.organisation?.id;

  const forbidManageBecauseSystemOrg = useMemo(
    () =>
      isSystemOrganisation(organisationId) &&
      !hasRight(rights, 'ORGANISATIONS.MANAGE.ORGANISATION') &&
      hasSomeRights(rights, [
        'ORGANISATIONS.MANAGE.ALL',
        'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
      ]),
    [rights, organisationId]
  );

  useEffect(() => {
    if (organisationId) dispatchOrganisationGet(organisationId);
  }, [organisationId, dispatchOrganisationGet]);

  useEffect(() => {
    if (organisationId) dispatchOrganisationAdminsGet(organisationId);
  }, [dispatchOrganisationAdminsGet, organisationId]);

  useEffect(() => {
    if (organisationId) dispatchOrganisationContactsGet(organisationId);
  }, [dispatchOrganisationContactsGet, organisationId]);

  const urlRoot = useMemo(() => (id ? `/organisations/${id}` : '/organisation'), [id]);

  const organisationName = useMemo(
    () => (organisationFetch.fulfilled ? organisationFetch.value.organisationName : '...'),
    [organisationFetch]
  );

  const { read: canReadIdCodes } = useCanAccessIdentificationCodes(organisationId);
  const canReadDocuments = hasRight(rights, 'principals/documents/read/organisation');

  const canSeePrincipals = hasSomeRights(rights, PRINCIPAL_RIGHTS);
  const canSeePendingInvitations = hasSomeRights(rights, PENDING_INVITATION_RIGHTS);
  const canSeeAppSubscriptions =
    hasSomeRights(rights, ORGANISATION_APPS_RIGHTS) && hasSomeRights(rights, COMMUNITY_APPS_RIGHTS);
  const canSeeUserGroups = hasSomeRights(rights, USER_GROUP_RIGHTS);
  const canSeeCommunities = hasSomeRights(rights, ORGANISATION_RIGHTS);
  const canSeeImpersonationRequests = hasSomeRights(rights, [
    'impersonation-requests/co-approve',
    'impersonation-requests/read/organisation',
  ]);

  const TAB_INDEX_MAPPING = useMemo(() => {
    let index = 0;
    const mapping = {};
    mapping[TABS.DETAILS] = index++;
    if (canSeePrincipals) mapping[TABS.PRINCIPALS] = index++;
    if (canSeePendingInvitations) mapping[TABS.PENDING_INVITATIONS] = index++;
    if (canSeePendingInvitations) mapping[TABS.JOIN_REQUESTS] = index++;
    if (canSeeAppSubscriptions) mapping[TABS.APP_SUBSCRIPTIONS] = index++;
    if (canSeeUserGroups) mapping[TABS.USER_GROUPS] = index++;
    if (canReadIdCodes) mapping[TABS.IDENTIFICATIONCODES] = index++;
    if (canReadDocuments) mapping[TABS.DOCUMENTS_OVERVIEW] = index++;
    if (canSeeCommunities) mapping[TABS.COMMUNITIES] = index++;
    if (canSeeImpersonationRequests) mapping[TABS.IMPERSONATION_REQUESTS] = index++;
    return mapping;
  }, [
    canSeePrincipals,
    canSeePendingInvitations,
    canSeeAppSubscriptions,
    canSeeUserGroups,
    canReadIdCodes,
    canReadDocuments,
    canSeeCommunities,
    canSeeImpersonationRequests,
  ]);

  return (
    <PageContent>
      <PageTitle flexed>
        {id ? (
          <Flex alignItems="center">
            <Link to="/organisations" href="/organisations" marginright="1px">
              {t('organisations')}
            </Link>
            &nbsp;/&nbsp;
            {organisationName}
          </Flex>
        ) : (
          <Flex alignItems="center">
            {t('myOrganisation')}&nbsp;-&nbsp;{organisationName}
          </Flex>
        )}
      </PageTitle>
      <Tab
        activeIndex={TAB_INDEX_MAPPING[tab]}
        onTabChange={(e, { activeIndex }) => {
          const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
          if (activeTab) {
            navigate(`${urlRoot}/${activeTab}`);
          }
        }}
        panes={[
          {
            menuItem: t('details'),
            render: () => (
              <Tab.Pane>
                <Details
                  organisationFetch={organisationFetch}
                  organisationAdminsFetch={organisationAdminsFetch}
                  organisationContactsFetch={organisationContactsFetch}
                  urlRoot={`${urlRoot}/${TABS.DETAILS}`}
                  organisationId={organisationId}
                  forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                />
              </Tab.Pane>
            ),
          },
          canSeePrincipals
            ? {
                menuItem: t('users'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <PrincipalsOverview
                        isOrgAdmin={!id}
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        allowDeleted={Boolean(id)}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canSeePendingInvitations
            ? {
                menuItem: t('tab.invitations'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <PendingInvitations
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canSeePendingInvitations
            ? {
                menuItem: t('joinRequests'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <JoinRequestsOverview
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canSeeAppSubscriptions
            ? {
                menuItem: t('tab.apps'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <AppSubscriptions
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canSeeUserGroups
            ? {
                menuItem: t('userGroups'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <UserGroupsOverview
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canReadIdCodes
            ? {
                menuItem: t('tab.idCodes'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <IdentificationCodesTable urlRoot={urlRoot} organisationId={organisationId} />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canReadDocuments
            ? {
                menuItem: t('tab.documents'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <DocumentsOverviewTab urlRoot={urlRoot} organisationId={organisationId} />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canSeeCommunities
            ? {
                menuItem: t('communities'),
                render: () => (
                  <Tab.Pane>
                    <OrganisationCommunities organisationId={organisationId} />
                  </Tab.Pane>
                ),
              }
            : undefined,
          canSeeImpersonationRequests
            ? {
                menuItem: t('tab.impersonation'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <ImpersonationRequestsTab organisationId={organisationId} urlRoot={urlRoot} />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          {
            menuItem: (
              <Menu.Menu position="right" key="menu">
                {canSeeCommunities &&
                  tab === TABS.DETAILS &&
                  id &&
                  !forbidManageBecauseSystemOrg && (
                    <Menu.Item
                      icon="pencil"
                      content={t('edit')}
                      onClick={() => navigate(`${urlRoot}/edit`)}
                    />
                  )}
              </Menu.Menu>
            ),
            render: () => null,
          },
        ].filter(Boolean)}
      />
    </PageContent>
  );
};

OrganisationDetails.propTypes = {
  dispatchOrganisationGet: PropTypes.func.isRequired,
  organisationFetch: PropTypes.object.isRequired,
  dispatchOrganisationAdminsGet: PropTypes.func.isRequired,
  organisationAdminsFetch: PropTypes.object.isRequired,
  dispatchOrganisationContactsGet: PropTypes.func.isRequired,
  organisationContactsFetch: PropTypes.object.isRequired,
};

export default compose(
  organisationService,
  organisationAdminsService,
  OrganisationContactsService
)(OrganisationDetails);
